import React from 'react';
import { router } from '@inertiajs/react';

import { Button } from '@/Components/Button';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { EllipseSvg } from '@/Components/Icons/EllipseSvg';
import { QuestionSvg } from '@/Components/Icons/QuestionSvg';
import { CoconutSvg } from '@/Components/Icons/CoconutSvg';
import { DangerSvg } from '@/Components/Icons/DangerSvg';
import { LemonSvg } from '@/Components/Icons/LemonSvg';

type ErrorProps = {
    status: number;
};

export const Error: React.FC<ErrorProps> = ({ status }) => {
    const errorData: Record<
        number,
        {
            title: string;
            subtitle: React.ReactNode;
            icons?: { icon: React.ReactNode; containerClassName?: string }[];
        }
    > = {
        503: {
            title: 'Bear with us, we’re making a few changes right now',
            subtitle: (
                <>
                    Check the{' '}
                    <a
                        href="http://status.reelflow.com"
                        target="_blank"
                        className="underline">
                        status page
                    </a>{' '}
                    for more details, or{' '}
                    <a href="mailto:help@reelflow.com" className="underline">
                        get in touch
                    </a>{' '}
                    if you require assistance from us.
                </>
            ),
            icons: [
                { icon: <DangerSvg />, containerClassName: 'top-3 right-22' },
                {
                    icon: <LemonSvg />,
                    containerClassName: 'bottom-2.5 left-24',
                },
            ],
        },
        500: {
            title: 'Sorry, the server is not responding',
            subtitle: 'Please try again in a few moments.',
            icons: [
                { icon: <DangerSvg />, containerClassName: 'top-3 right-22' },
                {
                    icon: <LemonSvg />,
                    containerClassName: 'bottom-2.5 left-24',
                },
            ],
        },
        404: {
            title: 'Sorry, we couldn’t find that page',
            subtitle: 'Check the page address.',
            icons: [
                { icon: <QuestionSvg />, containerClassName: 'top-3 right-22' },
                {
                    icon: <EllipseSvg />,
                    containerClassName: 'bottom-2 right-20',
                },
            ],
        },
        403: {
            title: 'Sorry, you can’t access this page',
            subtitle: 'Your user account doesn’t have the correct permissions.',
            icons: [
                { icon: <QuestionSvg />, containerClassName: 'top-3 right-22' },
                {
                    icon: <CoconutSvg />,
                    containerClassName: 'bottom-2 left-28',
                },
            ],
        },
    };

    const errorInfo = errorData[status] || {
        title: `Error ${status}`,
        subtitle: 'An unexpected error occurred.',
        icons: [],
    };

    const firstButtonLabel =
        status === 403 || status === 404
            ? 'Take me to the Dashboard'
            : 'Try again';

    const onUpdatePage = () => {
        if (status !== 403 && status !== 404) {
            router.reload({ only: [] });
        }
    };

    return (
        <div className="flex flex-col items-center justify-center h-screen p-6">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 top-0 left-10" />
                {errorInfo.icons?.map(({ icon, containerClassName }, index) => (
                    <div
                        key={index}
                        className={`absolute ${containerClassName || ''}`}>
                        {icon}
                    </div>
                ))}
            </div>
            <p className="mt-2 text-heading-two">{errorInfo.title}</p>
            <p className="mt-2 text-small-text-13">{errorInfo.subtitle}</p>
            <div className="mt-6 w-full flex flex-col space-y-3 items-center justify-center">
                {status === 403 || status === 404 ? (
                    <Button
                        elementType="a"
                        href="/dashboard"
                        variant="primary"
                        className="flex !items-center !justify-center px-6 py-2 transition w-96">
                        {firstButtonLabel}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={onUpdatePage}
                        className="flex !items-center !justify-center px-6 py-2 transition w-96">
                        {firstButtonLabel}
                    </Button>
                )}
                <Button
                    elementType="a"
                    target="_blank"
                    href="https://www.reelflow.com/"
                    variant="default"
                    className="!flex !items-center !justify-center px-6 py-2 transition w-96">
                    Take me to ReelFlow.com
                </Button>
            </div>
            <p className="mt-4 text-gray-500 text-sm">(Error code: {status})</p>
        </div>
    );
};
